import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, X } from "lucide-react";

const CATEGORIES = ["", "child_protection","education","health","livelihood","legal","other"];
const BANDS = ["", "low","medium","high","critical"];
const SEVS = BANDS;
const STATUSES = ["", "open","in_progress","referred","closed"];

export default function Workflows() {
  const [rules, setRules] = useState([]);
  const [showNew, setShowNew] = useState(false);

  const load = async () => { const { data } = await api.get("/workflows"); setRules(data); };
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    await api.patch(`/workflows/${r.id}`, { active: !r.active }); load();
  };
  const remove = async (id) => {
    if (!confirm("Delete this rule?")) return;
    await api.delete(`/workflows/${id}`); load();
  };

  return (
    <div className="p-8 md:p-10 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="overline">Automation</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight mt-2">Workflow Rules</h1>
          <p className="mt-2 text-sm text-slate-500">Auto-assign, escalate, and notify when cases match conditions.</p>
        </div>
        <button data-testid="new-rule-btn" onClick={() => setShowNew(true)}
          className="bg-[#0033A0] text-white px-5 py-3 flex items-center gap-2 hover:bg-slate-900">
          <Plus size={16} strokeWidth={2.5} /> New rule
        </button>
      </div>

      <div className="border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="p-4 text-left overline">Priority</th>
              <th className="p-4 text-left overline">Name</th>
              <th className="p-4 text-left overline">Match</th>
              <th className="p-4 text-left overline">Action</th>
              <th className="p-4 text-left overline">SLA</th>
              <th className="p-4 text-left overline">Active</th>
              <th className="p-4 text-left overline"></th>
            </tr>
          </thead>
          <tbody>
            {rules.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No rules yet. Create your first automation.</td></tr>}
            {rules.map(r => (
              <tr key={r.id} className="border-b border-slate-100 align-top">
                <td className="p-4 font-mono">{r.priority}</td>
                <td className="p-4 font-semibold">{r.name}</td>
                <td className="p-4 text-xs">
                  {r.match_category && <div>category: <span className="font-mono">{r.match_category}</span></div>}
                  {r.match_band && <div>band: <span className="font-mono">{r.match_band}</span></div>}
                  {r.match_severity && <div>severity: <span className="font-mono">{r.match_severity}</span></div>}
                  {!r.match_category && !r.match_band && !r.match_severity && <span className="text-slate-400">any</span>}
                </td>
                <td className="p-4 text-xs">
                  {r.assign_to_email && <div>assign → <span className="font-mono">{r.assign_to_email}</span></div>}
                  {r.set_status && <div>status → <span className="font-mono">{r.set_status}</span></div>}
                  {r.notify_emails?.length > 0 && <div>notify: <span className="font-mono">{r.notify_emails.join(", ")}</span></div>}
                </td>
                <td className="p-4 font-mono">{r.sla_hours ? `${r.sla_hours}h` : "—"}</td>
                <td className="p-4">
                  <button onClick={() => toggle(r)}
                    className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${r.active ? "bg-[#16A34A] text-white" : "bg-slate-400 text-white"}`}>
                    {r.active ? "On" : "Off"}
                  </button>
                </td>
                <td className="p-4">
                  <button onClick={() => remove(r.id)} className="text-red-600 hover:text-red-800"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewRule onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewRule({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", priority: 100, active: true,
    match_category: "", match_band: "", match_severity: "", match_assessment_band: "",
    assign_to_email: "", escalate_to_role: "", set_status: "", notify_emails: "",
    sla_hours: "",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      const payload = { ...form };
      ["match_category","match_band","match_severity","match_assessment_band","set_status","assign_to_email","escalate_to_role"].forEach(k => {
        if (!payload[k]) delete payload[k];
      });
      payload.priority = Number(payload.priority) || 100;
      payload.sla_hours = payload.sla_hours ? Number(payload.sla_hours) : null;
      payload.notify_emails = (form.notify_emails || "").split(",").map(e => e.trim()).filter(Boolean);
      await api.post("/workflows", payload);
      onCreated();
    } catch (e) { setErr(e.response?.data?.detail?.toString() || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl border border-slate-300 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div><div className="overline">Automation</div><h3 className="font-heading font-extrabold text-2xl">New workflow rule</h3></div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><label className="overline block mb-1">Name</label>
            <input data-testid="rule-name" required value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]" />
          </div>
          <div><label className="overline block mb-1">Priority (lower = first)</label>
            <input type="number" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono" />
          </div>
          <div><label className="overline block mb-1">SLA hours (optional)</label>
            <input type="number" min="0" value={form.sla_hours} onChange={e => setForm({...form, sla_hours: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono" />
          </div>

          <div className="md:col-span-2"><div className="overline mt-2">Match conditions</div></div>
          <div><label className="overline block mb-1">Category</label>
            <select value={form.match_category} onChange={e => setForm({...form, match_category: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              {CATEGORIES.map(c => <option key={c} value={c}>{c || "any"}</option>)}
            </select>
          </div>
          <div><label className="overline block mb-1">Risk band</label>
            <select value={form.match_band} onChange={e => setForm({...form, match_band: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              {BANDS.map(c => <option key={c} value={c}>{c || "any"}</option>)}
            </select>
          </div>
          <div><label className="overline block mb-1">Severity</label>
            <select value={form.match_severity} onChange={e => setForm({...form, match_severity: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              {SEVS.map(c => <option key={c} value={c}>{c || "any"}</option>)}
            </select>
          </div>
          <div><label className="overline block mb-1">Assessment band (RAG)</label>
            <select data-testid="rule-assessment-band" value={form.match_assessment_band} onChange={e => setForm({...form, match_assessment_band: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="">any</option>
              <option value="red">Red</option><option value="orange">Orange</option>
              <option value="yellow">Yellow</option><option value="green">Green</option>
            </select>
          </div>

          <div className="md:col-span-2"><div className="overline mt-2">Actions</div></div>
          <div><label className="overline block mb-1">Assign to user email</label>
            <input type="email" value={form.assign_to_email} onChange={e => setForm({...form, assign_to_email: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono text-sm" />
          </div>
          <div><label className="overline block mb-1">Escalate to role</label>
            <select value={form.escalate_to_role} onChange={e => setForm({...form, escalate_to_role: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              <option value="">—</option>
              {["case_worker","programme_manager","executive","government","admin"].map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div><label className="overline block mb-1">Set status</label>
            <select value={form.set_status} onChange={e => setForm({...form, set_status: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0]">
              {STATUSES.map(c => <option key={c} value={c}>{c || "—"}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="overline block mb-1">Notify emails (comma-separated)</label>
            <input value={form.notify_emails} onChange={e => setForm({...form, notify_emails: e.target.value})} className="w-full border border-slate-300 p-3 focus:outline-none focus:border-[#0033A0] font-mono text-sm" placeholder="lead@iscic.org, manager@ngo.org" />
          </div>

          {err && <div className="md:col-span-2 border border-red-300 bg-red-50 text-red-700 text-sm px-4 py-3">{err}</div>}
          <div className="md:col-span-2 flex justify-end gap-3 border-t border-slate-200 pt-4">
            <button type="button" onClick={onClose} className="border border-slate-300 px-5 py-3">Cancel</button>
            <button data-testid="rule-submit" disabled={saving} className="bg-[#0033A0] text-white px-5 py-3 hover:bg-slate-900 disabled:opacity-60">{saving ? "Saving…" : "Create rule"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
